/* eslint-disable react-hooks/exhaustive-deps */
import { Layout, Button, Grid } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import ContentPanel from "./ContentPanel";
import CreateEditModuleModal from "./Modals/CreateEditModuleModal";
import Sidebar from "./Sidebar";
import { showErrorNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";

const { Content, Sider } = Layout;

/**
 * Course module management dashboard. Below `lg` it renders a master/detail
 * single-pane flow (tree, then content) derived from `selectedNode` rather
 * than tracked as separate state; at `lg` and above it renders the
 * two-pane Sider + Content layout.
 * @param {{ ROOT_URL: string, adminConfig: object }} props
 */
const CourseModulesManagementDashboard = ({ ROOT_URL, adminConfig = {} }) => {
  const BASE_URL = "admin/courses/manage/module-management";
  const [selectedNode, setSelectedNode] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Static States
  const [courseEducators, setCourseEducators] = useState([]);
  const [lectureTypes, setLectureTypes] = useState([]);

  const screens = Grid.useBreakpoint();
  const isMobileView = screens.lg === false;

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
  };

  const handleBackToTree = () => {
    setSelectedNode(null);
  };

  const fetchAllModulesTree = () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/fetch-content-tree`)
      .then((res) => {
        const { modulesTree = [] } = res.data || {};
        const formattedTree = modulesTree.map((node) => ({
          ...node,
          key: node.uniquePublicId,
          type: node.contentType,
          children: (node.content || []).map((child) => ({
            ...child,
            key: child.uniquePublicId,
            type: child.contentType
          }))
        }));
        setTreeData(formattedTree);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleCreateModule = (values) => {
    setLoading(true);
    axios
      .post(`${ROOT_URL}/${BASE_URL}/create`, values)
      .then(() => {
        setIsCreateModalOpen(false);
        fetchAllModulesTree();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchFormRequisites = () => {
    setLoading(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/form-requisites`)
      .then((res) => {
        const { courseEducators = [], lectureTypes: fetchedLectureTypes = [] } =
          res.data || {};
        setCourseEducators(courseEducators);
        setLectureTypes(fetchedLectureTypes);
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAllModulesTree();
    fetchFormRequisites();
  }, []);

  useEffect(() => {
    if (selectedNode && treeData.length > 0) {
      let updatedNode = treeData.find(
        (node) => node.uniquePublicId === selectedNode.uniquePublicId
      );

      if (!updatedNode) {
        for (const node of treeData) {
          if (node.children) {
            const foundChild = node.children.find(
              (child) => child.uniquePublicId === selectedNode.uniquePublicId
            );
            if (foundChild) {
              updatedNode = foundChild;
              break;
            }
          }
        }
      }

      if (updatedNode && updatedNode !== selectedNode) {
        setSelectedNode(updatedNode);
      }
    }
  }, [treeData, selectedNode]);

  // Sidebar scrolls itself via flex:1 + minHeight:0, so it needs a real flex-column parent. Putting
  // display:flex on Sider's style would not work: that lands on the <aside>, whose only flex child is
  // antd's own children wrapper, leaving the button and the tree as inert grandchildren.
  const moduleTreePane = (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "10px", borderBottom: "1px solid #F0F0F0" }}>
        <Button type="primary" block onClick={() => setIsCreateModalOpen(true)}>
          + Create Module
        </Button>
      </div>
      <Sidebar onSelect={handleNodeSelect} treeData={treeData} selectedNode={selectedNode} />
    </div>
  );

  const contentPanel = (
    <ContentPanel
      selectedNode={selectedNode}
      refreshContentTree={fetchAllModulesTree}
      courseEducators={courseEducators}
      lectureTypes={lectureTypes}
      ROOT_URL={ROOT_URL}
      BASE_URL={BASE_URL}
      adminConfig={adminConfig}
      onBack={isMobileView ? handleBackToTree : undefined}
    />
  );

  if (isMobileView) {
    return (
      <Layout style={{ height: "calc(100vh - 64px)" }}>
        {selectedNode ? (
          <Content
            style={{
              padding: clamp(12, 24),
              margin: 0,
              minHeight: 280,
              height: "100%",
              overflow: "auto"
            }}
          >
            {contentPanel}
          </Content>
        ) : (
          moduleTreePane
        )}

        <CreateEditModuleModal
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateModule}
          loading={loading}
        />
      </Layout>
    );
  }

  return (
    <Layout style={{ height: "calc(100vh - 64px)" }}>
      {/* Subtract Navbar height, assuming ~64px */}
      <Sider
        width="25%"
        theme="light"
        style={{
          overflow: "auto",
          height: "100%",
          borderRight: "1px solid #000000"
        }}
      >
        {moduleTreePane}
      </Sider>
      <Layout>
        <Content
          style={{
            padding: clamp(12, 24),
            margin: 0,
            minHeight: 280,
            overflow: "auto"
          }}
        >
          {contentPanel}
        </Content>
      </Layout>

      <CreateEditModuleModal
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateModule}
        loading={loading}
      />
    </Layout>
  );
};

export default CourseModulesManagementDashboard;
